import { useEffect, useState } from "react";
import { StoreProductCardtype } from "../type";
import { useTranslation } from "react-i18next";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { REACT_APP_PAYPAL_MODE } from "env";
import { mixPostV1 } from "services/mixin";
import { toast } from "react-toastify";

const PAYPAL_CLIENT_ID =
  REACT_APP_PAYPAL_MODE === "live"
    ? "AZFX-UaOLaMkQqwVbbt7w0cInOZ5uANZ6UMrh4OY0mL0eA3m_ZSpXt1ubhu6F_Jn9EDbVgKqUIom8ZYH" // Live
    : "Abn2V5kg80p1v6NyUR2OnP4cpJ23R1b9sjPmNHKFzQvkcV4yos94dr19y8lJHEsAisLSrXlQzSxQGdxL"; // Sandbox

const initialOptions = {
  clientId: PAYPAL_CLIENT_ID,
  currency: "USD",
  intent: "capture",
  components: "buttons",
  "enable-funding": "venmo,paylater",
  "disable-funding": "",
};
interface BuyProductPopUpProps extends StoreProductCardtype {
  onClose?: () => void;
}

const BuyProductPopUp: React.FC<BuyProductPopUpProps> = (product) => {
  const { t } = useTranslation();
  const [userId, setUserId] = useState<number>();
  const [steps, setSteps] = useState("viewInfo"); // viewInfo, payment, success , error
  const [email, setEmail] = useState("");

  const [orderId, setOrderId] = useState<string>("");
  const [paymentId, setPaymentId] = useState<number>();
  async function handlePayment() {
    // Handle payment logic here
    setSteps("payment");
    // Simulate payment processing
    setTimeout(() => {
      const isSuccess = Math.random() > 0.5; // Randomly simulate success or failure
      if (isSuccess) {
        setSteps("success");
      } else {
        setSteps("error");
      }
    }, 3000);
  }
  useEffect(() => {
    const localeData = localStorage.getItem("user");

    if (localeData) {
      const idData = JSON.parse(localeData).id;
      setUserId(idData);
    }
  }, []);
  if (steps === "viewInfo") {
    return (
      <div
        className="bg-white w-full p-6 max-h-[80vh] overflow-y-auto"
        style={{ scrollbarWidth: "none" }}
      >
        {/* Product Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-4">
            <div>
              <img
                src={product.image}
                alt={product.name}
                className="w-20 h-20 rounded-lg object-cover"
                loading="lazy"
                onError={e => {
                  const target = e.currentTarget;
                  target.onerror = null;
                  target.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = "fallback-image w-20 h-20 rounded-lg flex items-center justify-center bg-gray-100";
                  fallback.innerHTML = `
                      <div class="flex flex-col items-center">
                          <p class="mt-2 text-sm text-gray-500">Image not available</p>
                      </div>
                  `;
                  target.parentNode?.appendChild(fallback);
                }}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="!text-lg font-semibold text-gray-900 !mb-0">
                  {product.name}
                </h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  {product.platform && (
                    <div className="bg-purple-500 text-white px-2 py-1 rounded !text-xs font-semibold">
                      🚀 {product.platform}
                    </div>
                  )}
                  {product.indicator && (
                    <div className="bg-orange-500 text-white px-2 py-1 rounded !text-xs font-semibold">
                      {product.indicator}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                {product.star && (
                  <div className="flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-star h-4 w-4 text-yellow-400 fill-current"
                    >
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                    <span className="font-medium">{product.star}</span>
                    {product.review && (
                      <span className="text-gray-500">
                        ({product.review} {t("store_detail_product_reviews")})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Price Breakdown */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-700">
              {t("store_pay_product_price")}:
            </span>
            <div className="flex items-center gap-2">
              {product.discount && (
                <span className="text-gray-500 line-through">
                  {product.price}
                </span>
              )}
              <span className="text-2xl font-bold text-gray-900">
                {product.finalPrice} USD
              </span>
            </div>
          </div>
          {product.discount && (
            <div className="flex items-center justify-between mb-3 text-green-600">
              <span>{t("store_pay_discount")}:</span>
              <span className="font-semibold">-{product.discount}</span>
            </div>
          )}
          <div className="border-t border-blue-200 pt-3">
            <div className="flex items-center justify-between !text-lg font-bold">
              <span>{t("store_pay_total")}:</span>
              <span className="text-blue-600">{product.finalPrice} USD</span>
            </div>
            <p className="text-xs text-gray-500 mt-1 !mb-0">
              {t("store_pay_footer1")}
            </p>
          </div>
        </div>
        {/* What's Included */}
        <div className="mb-6">
          <h4 className="!text-base font-semibold text-gray-900 mb-3">
            {t("store_pay_inclues_title")}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 text-green-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-check h-4 w-4"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
              <span>{t("store_pay_inclues_1")}</span>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-check h-4 w-4"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
              <span>{t("store_pay_inclues_2")}</span>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-check h-4 w-4"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
              <span>{t("store_pay_inclues_3")}</span>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-check h-4 w-4"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
              <span>{t("store_pay_inclues_4")}</span>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-check h-4 w-4"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
              <span>{t("store_pay_inclues_5")}</span>
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex flex-col gap-3">

          <PayPalScriptProvider options={initialOptions}>
            <PayPalButtons
              style={{
                shape: "rect",
                layout: "vertical",
                color: "gold",
                label: "paypal",
              }}
              createOrder={async () => {
                try {
                  const orderData = await mixPostV1(
                    "/api/v1/be/order-tools/paypal/create",
                    {
                      user_id: userId,
                      tool_id: Number(product.id),
                      return_url: `${window.location.origin}/stores`,
                      cancel_url: `${window.location.origin}/stores/${product.id}`,
                      quantity: 1,
                    }
                  );

                  // const orderData = await mixPostV1('/api/v1/be/orders/paypal/create', {
                  //     user_id: userId,
                  //     product_id: 205,
                  //     return_url: `${window.location.origin}/stores`,
                  //     cancel_url: `${window.location.origin}/stores/${id}`,
                  //     quantity: 1
                  // });
                  if (orderData?.status) {
                    setOrderId(orderData.data.order_id);
                    setPaymentId(orderData.data.payment_id);
                    return orderData.data.order_id;
                  } else {
                    if (
                      !orderData.status &&
                      orderData.error.already_purchased
                    ) {
                      toast.error(orderData.error.already_purchased, {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light",
                      });
                    }
                    // const errorDetail = orderData?.details?.[0];
                    // const errorMessage = errorDetail
                    //     ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
                    //     : JSON.stringify(orderData);
                    // throw new Error(errorMessage);
                  }
                } catch (error: any) {
                  toast.error("There is an error, please try again", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                  });
                  throw error;
                }
              }}
              onApprove={async (data, actions) => {
                try {
                  // Sử dụng data.orderID trực tiếp, không dùng state
                  const orderData = await mixPostV1(
                    "/api/v1/be/order-tools/paypal/capture",
                    {
                      paypal_order_id: orderId,
                      payment_id: paymentId,
                    }
                  );

                  // const orderData = await mixPostV1('/api/v1/be/orders/paypal/capture', {
                  //     paypal_order_id: orderId,
                  //     payment_id: paymentId,
                  // });

                  if (!orderData || typeof orderData !== "object") {
                    throw new Error("Invalid response from server");
                  }

                  const errorDetail = orderData?.data;
                  if (orderData && orderData.status) {
                    toast.success("Payment successfully", {
                      position: "top-right",
                      autoClose: 3000,
                      hideProgressBar: false,
                      closeOnClick: false,
                      pauseOnHover: true,
                      draggable: true,
                      progress: undefined,
                      theme: "light",
                    });
                  }

                  console.log(errorDetail);

                  if (errorDetail?.issue === "INSTRUMENT_DECLINED") {
                    return actions.restart();
                  } else if (errorDetail) {
                    // throw new Error(`${errorDetail.description} (${orderData.debug_id})`);
                    return;
                  } else if (orderData && orderData.data) {
                    const transaction = orderData.data;

                    toast.error(
                      `Transaction ${transaction.status}: ${transaction.capture_id}.`,
                      {
                        position: "top-right",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light",
                      }
                    );

                    console.log("Capture result", orderData);
                  }
                } catch (error) {
                  console.error(error);
                  toast.error(
                    `Sorry, your transaction could not be processed...${error}`,
                    {
                      position: "top-right",
                      autoClose: 3000,
                      hideProgressBar: false,
                      closeOnClick: false,
                      pauseOnHover: true,
                      draggable: true,
                      progress: undefined,
                      theme: "light",
                    }
                  );
                }
              }}
            />
          </PayPalScriptProvider>
          <button
            type="button"
            className="flex-1 px-6 py-2 border border-gray-300 text-gray-700 !rounded-lg hover:bg-gray-50 transition duration-300 font-semibold"
            onClick={() => {
              if (product.onClose) {
                product.onClose();
              }
            }}
          >
            {t("Cancel")}
          </button>
        </div>
        <div className="text-center mt-4">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-alert-circle h-4 w-4"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" x2="12" y1="8" y2="12"></line>
              <line x1="12" x2="12.01" y1="16" y2="16"></line>
            </svg>
            <span>{t("store_pay_footer2")} support@lumir.finance</span>
          </div>
        </div>
      </div>
    );
  }
  if (steps === "payment") {
    return (
      <div className="bg-white w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl w-full text-center font-bold text-gray-900">
            {t("pay_prorcessing")}
          </h3>
        </div>
        <div className="flex flex-col items-center gap-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-loader h-12 w-12 text-blue-600 animate-spin"
          >
            <line x1="12" x2="12" y1="2" y2="6"></line>
            <line x1="12" x2="12" y1="18" y2="22"></line>
            <line x1="4.93" x2="7.76" y1="4.93" y2="7.76"></line>
            <line x1="16.24" x2="19.07" y1="16.24" y2="19.07"></line>
            <line x1="2" x2="6" y1="12" y2="12"></line>
            <line x1="18" x2="22" y1="12" y2="12"></line>
            <line x1="4.93" x2="7.76" y1="19.07" y2="16.24"></line>
            <line x1="16.24" x2="19.07" y1="7.76" y2="4.93"></line>
          </svg>
          <p className="text-gray-600 text-center">
            {t("pay_prorcessing_desc")}
          </p>
        </div>
      </div>
    );
  } else if (steps === "error") {
    return (
      <div className="bg-white w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl w-full text-center font-bold text-gray-900">
            {t("pay_failed")}
          </h3>
        </div>
        <div className="flex flex-col items-center gap-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-alert-circle h-12 w-12 text-red-600"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" x2="12" y1="8" y2="12"></line>
            <line x1="12" x2="12.01" y1="16" y2="16"></line>
          </svg>
          <p className="text-gray-600 text-center">{t("pay_failed_desc")}</p>
          <button
            onClick={() => setSteps("viewInfo")}
            className="mt-4 bg-blue-600 text-white px-4 py-2 !rounded-lg hover:bg-blue-700 transition duration-300"
          >
            {t("retryPayment")}
          </button>
        </div>
      </div>
    );
  } else if (steps === "success") {
    return (
      <div className="bg-white w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl w-full text-center font-bold text-gray-900">
            {t("pay_success")}
          </h3>
        </div>
        <div className="flex flex-col items-center gap-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-check-circle h-12 w-12 text-green-600"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9 12l2 2l4 -4"></path>
          </svg>
          <p className="text-gray-600 text-center">
            {t("pay_success_desc", { name: product.name })}
          </p>
          <button
            onClick={() => (window.location.href = "/downloads")}
            className="mt-4 bg-blue-600 text-white px-4 py-2 !rounded-lg hover:bg-blue-700 transition duration-300"
          >
            {t("downloadBotPageRedirect")}
          </button>
        </div>
      </div>
    );
  }
  return null;
};

export default BuyProductPopUp;
